package com.evoapps.evoshift.di

import androidx.lifecycle.ViewModel
import androidx.lifecycle.ViewModelProvider
import androidx.lifecycle.ViewModelStore
import androidx.lifecycle.viewmodel.initializer
import androidx.lifecycle.viewmodel.viewModelFactory
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.shell.WindowTarget
import com.evoapps.evoshift.viewmodels.DashboardViewModel
import com.evoapps.evoshift.viewmodels.NotificationsViewModel
import com.evoapps.evoshift.viewmodels.PendingRequestsViewModel
import org.kodein.di.DI
import org.kodein.di.bind
import org.kodein.di.instance
import org.kodein.di.singleton
import org.kodein.di.subDI

/**
 * SH-161 F3: a window of its own ("Open in new window") with its own workplace and month: the
 * app's container with its own [WorkplaceSessionManager] (not remembered across restarts: that is
 * the main window's), [WorkplaceMonthSelection] and the app-wide view models that follow them
 * (the dashboard, the bell, the request badges). Everything else is the app's: the providers are
 * built in this container (so the screens' view models see this window's workplace), the other
 * singletons are shared.
 *
 * Kept in the window's own ViewModelStore (App.kt), so it outlives a configuration change; once
 * the window closes [onCleared] clears its view models and their listeners.
 */
class AppWindowScope(target: WindowTarget, parent: DI = appModule) : ViewModel() {
    private val viewModels = ViewModelStore()

    val di: DI = subDI(parent, allowSilentOverride = true) {
        bind<WorkplaceSessionManager>() with singleton {
            WorkplaceSessionManager().also { it.setWorkplaceId(target.workplaceId) }
        }
        bind<WorkplaceMonthSelection>() with singleton {
            WorkplaceMonthSelection(instance()).also { selection -> target.month?.let(selection::selectMonth) }
        }
        bind<DashboardViewModel>() with singleton { keep { newDashboardViewModel() } }
        bind<NotificationsViewModel>() with singleton { keep { newNotificationsViewModel() } }
        bind<PendingRequestsViewModel>() with singleton { keep { newPendingRequestsViewModel() } }
    }

    /** [create]'s view model, held by this window (cleared with it). */
    private inline fun <reified VM : ViewModel> keep(crossinline create: () -> VM): VM =
        ViewModelProvider.create(viewModels, viewModelFactory { initializer { create() } })[VM::class]

    override fun onCleared() {
        viewModels.clear()
    }
}
